import { useMemo } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Space, Tag, Typography } from 'antd';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { ColumnsType } from 'antd/es/table';
import type { AssetDetailIncident } from '~/app/services/asset/asset.service';
import { useTranslation } from 'react-i18next';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { formatDateTime } from './assetDetail.utils';
import { canNavigateToAssetIncidentDetails } from './assetDetailIncidentNavigation';
import { buildIncidentDetailPath } from './assetIncidentRoutes';

const { Paragraph, Text } = Typography;

export function useAssetDetailIncidentColumns(): ColumnsType<AssetDetailIncident> {
  const { t } = useTranslation('assets');
  const { hasPermission } = usePermission();
  const canOpenIncidentDetails =
    canNavigateToAssetIncidentDetails(hasPermission);

  return useMemo(() => {
    const columns: ColumnsType<AssetDetailIncident> = [
      {
        title: t('detail.incidents.table.title', 'Incident'),
        key: 'title',
        render: (_value: unknown, incident: AssetDetailIncident) => {
          const code = incident.code.trim();

          return (
            <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
              <Text
                strong
                ellipsis={{ tooltip: code || undefined }}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: '#191c1f',
                }}
              >
                {code || '—'}
              </Text>
              <Text
                strong
                ellipsis={{ tooltip: incident.title || undefined }}
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: '#191c1f',
                }}
              >
                {incident.title}
              </Text>
              {incident.description ? (
                <Paragraph
                  type="secondary"
                  ellipsis={{ rows: 2, tooltip: incident.description }}
                  style={{
                    margin: '2px 0 0',
                    fontSize: '0.75rem',
                    color: 'var(--evimo-text-muted)',
                  }}
                >
                  {incident.description}
                </Paragraph>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: t('detail.incidents.table.status', 'Status'),
        dataIndex: 'status',
        key: 'status',
        render: (value: string) => <Tag>{value}</Tag>,
      },
      {
        title: t('detail.incidents.table.priority', 'Priority'),
        dataIndex: 'priority',
        key: 'priority',
        render: (value: string | null) => value ?? '-',
      },
      {
        title: t('detail.incidents.table.severity', 'Severity'),
        dataIndex: 'severity',
        key: 'severity',
        render: (value: number | null) => value ?? '-',
      },
      {
        title: t('detail.incidents.table.createdAt', 'Created at'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: t(
          'detail.incidents.table.affectedComponents',
          'Affected components',
        ),
        dataIndex: 'affectedComponents',
        key: 'affectedComponents',
        render: (value: AssetDetailIncident['affectedComponents']) =>
          value.length
            ? value.map((component) => component.name).join(', ')
            : '-',
      },
    ];

    if (canOpenIncidentDetails) {
      columns.push({
        title: t('detail.incidents.table.actions', 'Actions'),
        key: 'actions',
        width: 72,
        align: 'center',
        render: (_value: unknown, incident: AssetDetailIncident) => {
          const label = t(
            'detail.incidents.openDetails',
            'Open incident details',
          );
          return (
            <TableRowActionsMenu
              ariaLabel={label}
              items={[
                {
                  key: 'open',
                  label,
                  icon: <EyeOutlined />,
                  onClick: () => {
                    window.open(
                      buildIncidentDetailPath(incident.id),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  },
                },
              ]}
            />
          );
        },
      });
    }

    return columns;
  }, [t, canOpenIncidentDetails]);
}
